import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Clock, ArrowLeft, ArrowRight, Eye, EyeOff, Shield, Building2, User, WifiOff, Smartphone, Handshake } from "lucide-react";
import PWAInstallPrompt from "../components/PWAInstallPrompt";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // Permite acesso direto ao login de superadmin via ?role=superadmin
  const roleFromUrl = searchParams.get("role");
  const [selectedRole, setSelectedRole] = useState(roleFromUrl === "superadmin" ? "superadmin" : null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Monitorar status da conexão
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      
      if (!navigator.onLine) {
        toast.success(`Bem-vindo, ${user.name}! (Modo Offline)`);
      } else {
        toast.success(`Bem-vindo, ${user.name}!`);
      }
      
      // Redirecionar baseado no role
      if (user.role === "superadmin") {
        navigate("/superadmin");
      } else if (user.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      const message = error.response?.data?.detail || error.message || "Erro ao fazer login";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    {
      id: "superadmin",
      title: "Administrador Master",
      description: "Gerencia todas as empresas do sistema",
      icon: Shield,
      color: "teal",
      bgColor: "bg-[#1CA1BA]",
      hoverColor: "hover:bg-[#17889E]",
      lightBg: "bg-cyan-50",
      borderColor: "border-[#1CA1BA]",
      textColor: "text-[#17889E]"
    },
    {
      id: "admin",
      title: "Empresa",
      description: "Gerencia funcionários e relatórios",
      icon: Building2,
      color: "teal",
      bgColor: "bg-[#17889E]",
      hoverColor: "hover:bg-[#137082]",
      lightBg: "bg-cyan-50",
      borderColor: "border-cyan-600",
      textColor: "text-[#137082]"
    },
    {
      id: "employee",
      title: "Funcionário",
      description: "Registra ponto e consulta horas",
      icon: User,
      color: "green",
      bgColor: "bg-[#1CA1BA]",
      hoverColor: "hover:bg-[#1CA1BA]-dark",
      lightBg: "bg-green-50",
      borderColor: "border-[#1CA1BA]",
      textColor: "text-[#17889E]"
    },
    {
      id: "affiliate",
      title: "Afiliado",
      description: "Indique e ganhe comissões recorrentes",
      icon: Handshake,
      color: "purple",
      bgColor: "bg-[#1CA1BA]",
      hoverColor: "hover:bg-[#17889E]",
      lightBg: "bg-purple-50",
      borderColor: "border-purple-500",
      textColor: "text-purple-600"
    }
  ];

  const selectedRoleData = roles.find(r => r.id === selectedRole);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="p-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Voltar ao início
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <img 
                src="/logo-ciaponto-color.png" 
                alt="CiaPonto" 
                className="h-12 w-auto"
              />
            </Link>
            <h1 className="font-heading font-bold text-2xl text-slate-900 mt-4">Acesse o Sistema</h1>
            <p className="text-slate-600">Selecione sua área de acesso</p>
          </div>

          {!selectedRole ? (
            /* Role Selection - Oculta "superadmin" da UI, acesso apenas via URL direta */
            <div className="grid md:grid-cols-3 gap-4 mb-8 max-w-3xl mx-auto">
              {roles.filter(role => role.id !== "superadmin").map((role) => (
                <Card 
                  key={role.id}
                  className={`border-2 cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${
                    selectedRole === role.id ? `${role.borderColor} ${role.lightBg}` : "border-slate-200 hover:border-slate-300"
                  }`}
                  onClick={() => {
                    if (role.id === "admin") {
                      window.open("https://ciaponto.vercel.app/", "_blank");
                    } else if (role.id === "employee") {
                      setSelectedRole("employee_download");
                    } else if (role.id === "affiliate") {
                      navigate("/indica/login");
                    } else {
                      setSelectedRole(role.id);
                    }
                  }}
                  data-testid={`role-${role.id}-btn`}
                >
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 ${role.lightBg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <role.icon className={`w-8 h-8 ${role.textColor}`} />
                    </div>
                    <h3 className="font-heading font-bold text-lg text-slate-900 mb-1">{role.title}</h3>
                    <p className="text-sm text-slate-500">{role.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : selectedRole === "employee_download" ? (
            /* App Download Section for Employees */
            <div className="max-w-md mx-auto">
              <Card className="border-2 border-[#1CA1BA] shadow-lg">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Smartphone className="w-8 h-8 text-[#17889E]" />
                  </div>
                  <CardTitle className="font-heading text-xl">Baixe o App CiaPonto</CardTitle>
                  <CardDescription>Registre seu ponto diretamente pelo celular</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 px-6 pb-6">
                  <a 
                    href="https://play.google.com/store/apps/details?id=br.com.pontogo.colaboradorciaponto&hl=pt_BR"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 border-2 border-slate-200 rounded-xl hover:border-green-400 hover:shadow-md transition-all cursor-pointer"
                    data-testid="download-android-btn"
                  >
                    <img src="/icon-android.png" alt="Android" className="w-14 h-14 rounded-xl" />
                    <div className="flex-1">
                      <p className="font-bold text-slate-900">Android</p>
                      <p className="text-sm text-slate-500">Baixar na Google Play</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400" />
                  </a>

                  <a 
                    href="https://apps.apple.com/br/app/ciaponto-colaborador/id6760658182"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 border-2 border-slate-200 rounded-xl hover:border-gray-400 hover:shadow-md transition-all cursor-pointer"
                    data-testid="download-ios-btn"
                  >
                    <img src="/icon-apple.png" alt="iOS" className="w-14 h-14 rounded-xl" />
                    <div className="flex-1">
                      <p className="font-bold text-slate-900">iPhone / iPad</p>
                      <p className="text-sm text-slate-500">Baixar na App Store</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400" />
                  </a>

                  <button 
                    onClick={() => setSelectedRole(null)}
                    className="w-full text-center text-sm text-slate-500 hover:text-[#1CA1BA] transition-colors mt-4 flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Voltar e escolher outra área
                  </button>
                </CardContent>
              </Card>
            </div>
          ) : (
            /* Login Form */
            <div className="max-w-md mx-auto">
              <Card className={`border-2 ${selectedRoleData?.borderColor} shadow-lg`}>
                <CardHeader className="text-center pb-4">
                  <div className={`w-16 h-16 ${selectedRoleData?.lightBg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    {selectedRoleData && <selectedRoleData.icon className={`w-8 h-8 ${selectedRoleData.textColor}`} />}
                  </div>
                  <CardTitle className="font-heading text-xl">{selectedRoleData?.title}</CardTitle>
                  <CardDescription>{selectedRoleData?.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Aviso de Modo Offline */}
                  {!isOnline && (
                    <div className="mb-4 bg-orange-100 border border-orange-300 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-orange-800">
                        <WifiOff className="w-4 h-4" />
                        <span className="text-sm font-medium">Modo Offline</span>
                      </div>
                      <p className="text-xs text-orange-700 mt-1">
                        Você pode entrar se já fez login neste dispositivo antes.
                      </p>
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">
                        {selectedRole === "superadmin" ? "Usuário" : "Email"}
                      </Label>
                      <Input
                        id="email"
                        type={selectedRole === "superadmin" ? "text" : "email"}
                        placeholder={selectedRole === "superadmin" ? "Digite seu usuário" : "seu@email.com"}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="login-email-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password">Senha</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          data-testid="login-password-input"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className={`w-full ${selectedRoleData?.bgColor} ${selectedRoleData?.hoverColor} h-11`}
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? "Entrando..." : "Entrar"}
                    </Button>
                  </form>

                  <div className="mt-4">
                    <Button
                      variant="ghost"
                      className="w-full text-slate-500"
                      onClick={() => {
                        setSelectedRole(null);
                        setEmail("");
                        setPassword("");
                      }}
                      data-testid="back-to-roles-btn"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Voltar e escolher outra área
                    </Button>
                  </div>

                  {/* Help text based on role */}
                  <div className={`mt-4 p-4 ${selectedRoleData?.lightBg} rounded-lg`}>
                    {selectedRole === "superadmin" && (
                      <p className="text-xs text-slate-600 text-center">
                        <strong>Administrador Master:</strong> Acesso exclusivo para gerenciar todas as empresas do sistema CiaPonto.
                      </p>
                    )}
                    {selectedRole === "admin" && (
                      <p className="text-xs text-slate-600 text-center">
                        <strong>Empresa:</strong> Use o email e senha fornecidos pelo administrador do CiaPonto ao contratar o sistema.
                      </p>
                    )}
                    {selectedRole === "employee" && (
                      <div className="text-center">
                        <p className="text-xs text-slate-600 mb-3">
                          <strong>Funcionário:</strong> Use seu email e senha cadastrados.
                        </p>
                        <Link 
                          to="/register/employee" 
                          className="inline-flex items-center gap-2 text-sm text-green-600 hover:text-green-700 font-medium"
                          data-testid="employee-register-link"
                        >
                          Não tem conta? Cadastre-se aqui
                        </Link>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {selectedRole === "admin" && (
                <div className="mt-4 text-center">
                  <p className="text-sm text-slate-600">
                    Quer contratar o sistema?{" "}
                    <Link to="/#contact" className="text-[#17889E] hover:text-[#137082] font-medium">
                      Entre em contato
                    </Link>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      
      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </div>
  );
}
